import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { isStandalone } from "@/lib/standalone";

const GIS_SRC = "https://accounts.google.com/gsi/client";

interface GoogleCredentialResponse {
    credential?: string;
}
interface GoogleIdInitConfig {
    client_id: string;
    callback?: (r: GoogleCredentialResponse) => void;
    ux_mode?: "popup" | "redirect";
    login_uri?: string;
}
interface GoogleIdApi {
    accounts: {
        id: {
            initialize: (config: GoogleIdInitConfig) => void;
            renderButton: (el: HTMLElement, opts: Record<string, unknown>) => void;
        };
    };
}

function loadGis(): Promise<GoogleIdApi | null> {
    return new Promise((resolve) => {
        const w = window as unknown as { google?: GoogleIdApi };
        if (w.google?.accounts?.id) {
            resolve(w.google);
            return;
        }
        const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SRC}"]`);
        const onLoad = () => resolve((window as unknown as { google?: GoogleIdApi }).google ?? null);
        if (existing) {
            existing.addEventListener("load", onLoad);
            return;
        }
        const s = document.createElement("script");
        s.src = GIS_SRC;
        s.async = true;
        s.defer = true;
        s.addEventListener("load", onLoad);
        s.addEventListener("error", () => resolve(null));
        document.head.appendChild(s);
    });
}

/**
 * Renders the official Google sign-in button when VITE_GOOGLE_CLIENT_ID is
 * configured. Otherwise renders nothing so password login stays the only
 * option (e.g. during local testing before the Google app is set up).
 */
export function GoogleSignInButton() {
    const clientId = (import.meta.env as Record<string, string | undefined>).VITE_GOOGLE_CLIENT_ID;
    const { loginWithGoogle } = useAuth();
    const navigate = useNavigate();
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!clientId || !ref.current) return;
        let cancelled = false;
        void loadGis().then((google) => {
            if (cancelled || !google || !ref.current) return;
            // Installed iOS PWAs (display: standalone) cripple window.open, so the
            // default popup flow never delivers the credential back via
            // window.opener.postMessage — the account chooser just goes blank.
            // FedCM is unsupported on iOS Safari, so fall back to a full-page
            // redirect: Google POSTs the ID token to our server endpoint, which
            // verifies it and redirects back into the SPA. Normal browsers keep
            // the popup flow (it works and avoids a full reload).
            if (isStandalone()) {
                google.accounts.id.initialize({
                    client_id: clientId,
                    ux_mode: "redirect",
                    login_uri: `${window.location.origin}/api/auth/google/redirect`,
                });
            } else {
                google.accounts.id.initialize({
                    client_id: clientId,
                    callback: (resp) => {
                        if (!resp.credential) return;
                        void loginWithGoogle(resp.credential)
                            .then(() => navigate("/"))
                            .catch(() => {
                                /* error surfaced via auth context */
                            });
                    },
                });
            }
            google.accounts.id.renderButton(ref.current, { theme: "outline", size: "large", width: 320 });
        });
        return () => {
            cancelled = true;
        };
    }, [clientId, loginWithGoogle, navigate]);

    if (!clientId) return null;
    return (
        <div className="mt-4">
            <div className="relative my-3 text-center">
                <span className="bg-card px-2 text-xs text-foreground/60 relative z-10">or</span>
                <div className="absolute inset-x-0 top-1/2 h-px bg-border" />
            </div>
            <div ref={ref} className="flex justify-center" />
        </div>
    );
}
